<template>
  <form id="donation-details" class="card w-modal h-modal">
    <div class="header">
      <div class="title">
        <div class="flex flex-col">
          <h1 class="mb-2">New Donation</h1>
          <div class="flex flex-row">
            <h4 class="opacity-50">Supporter Details</h4>
            <h4 class="ml-4 mr-4">></h4>
            <h4>Donation Details</h4>
            <h4 class="ml-4 mr-4" v-if="showBbmsPopup">></h4>
            <h4 v-if="showBbmsPopup">Payment</h4>
          </div>
        </div>
      </div>
      <div class="close" @click="close()">
        <i class="icn icn-x sm"></i>
      </div>
    </div>
    <div class="content" v-if="isLoadingForm">
      <div class="flex flex-col justify-center items-center h-full">
        <img class="h-36" src="@/assets/img/loading.svg" />
        <h2>Loading</h2>
      </div>
    </div>

    <div class="content" v-if="!isLoadingForm">
      <!-- DONATION DOURCE -->

      <div class="field">
        <label>Donation Source</label>
        <div class="row mt-2">
          <div class="field">
            <select required name="donation-source" v-model="payload.donationSource">
              <option value disabled selected>Select a donation source</option>
              <option value="Phone call">Phone call</option>
              <option value="Mail post">Mail post</option>
            </select>
          </div>
        </div>
        <div class="row mt-2">
          <div class="field">
            <input disabled v-if="!payload.donationProductDetails" value="No product found" type="text" />
            <select
              name="product"
              v-if="payload.donationProductDetails"
              v-model="payload.donationProductDetails"
              @change="updateDonationSettings()"
            >
              <option v-for="(product, index) in allProducts" :key="index" :value="product">
                {{ product.productname }}
              </option>
            </select>
          </div>
        </div>

        <!-- GIFT TYPE -->

        <div class="row mt-2" v-if="payload.donationProductDetails">
          <div class="field">
            <label>Gift Type</label>
          </div>
        </div>
        <div class="field">
          <div class="row mt-2">
            <input disabled :value="toTitleCase(payload.donationProductDetails.producttype)" type="text" />
          </div>
        </div>
        <div class="row mt-2">
          <div class="field">
            <select
              v-if="payload.donationProductDetails.donationtypes"
              :disabled="payload.donationProductDetails.donationtypes.length == 1"
              v-model="payload.donation.type"
              @change="getMinDonationAmount()"
            >
              <option
                v-for="donationType in payload.donationProductDetails.donationtypes"
                :key="donationType"
                :value="donationType"
                :selected="payload.donation.type === donationType"
              >
                {{ parseDonationType(donationType) }}
              </option>
            </select>
          </div>
        </div>
        <div class="row mt-2" v-if="payload.donationMinAmount">
          <div class="field">
            <label>Minimum</label>
            <input disabled :value="`$${payload.donationMinAmount}`" type="text" />
          </div>
        </div>

        <!-- PACKAGES -->
        <div class="row mt-2" v-if="hasPackages">
          <div class="field">
            <label>Packages</label>
            <div class="flex flex-row ml-8 mt-2">
              <div class="flex flex-col justify-start">
                <label v-for="(pack, index) in payload.donationProductDetails.packages" :key="index">
                  <div v-if="pack.display">
                    <input
                      class="mr-2"
                      :id="pack.code"
                      type="radio"
                      :value="index"
                      v-model="selectedPackageIndex"
                      @change="updatePackageCode()"
                    />
                    {{ pack.display }}
                  </div>

                  <Alert v-else :message="errorPackage(pack.code)"></Alert>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-2" v-if="payload.donationProductDetails.producttype === 'adoption' && hasPackages">
        <div class="field">
          <label>
            <input v-model="payload.donation.isGift" type="checkbox" @change="clearGiftDetails()" />
            Make this a gift for someone
          </label>
        </div>
      </div>

      <!-- GIFT -->
      <div v-if="showGiftDetails" class="row mt-2">
        <div class="w-1/4">
          <select required v-model="payload.gift.title">
            <option value disabled>Select a title</option>
            <option value="Mr">Mr</option>
            <option value="Mrs">Mrs</option>
            <option value="Ms">Ms</option>
            <option value="Miss">Miss</option>
            <option value="Dr">Dr</option>
            <option value="Mx">Mx</option>
          </select>
        </div>
      </div>
      <div v-if="showGiftDetails" class="row mt-2">
        <div class="field">
          <input
            required
            v-model="payload.gift.firstName"
            type="text"
            placeholder="First name"
            autocomplete="nope"
            maxlength="127"
          />
        </div>
        <div class="field">
          <input
            required
            v-model="payload.gift.lastName"
            type="text"
            placeholder="Last name"
            autocomplete="nope"
            maxlength="127"
          />
        </div>
      </div>

      <!-- GIFT ADDRESS -->
      <div v-if="showGiftDetails" class="row mt-2">
        <div class="field">
          <label>
            <input v-model="payload.donation.isGiftAtSameAddress" type="checkbox" @change="copyGiftAddress()" />
            The gift is to be delivered to the supporter address
          </label>
        </div>
      </div>
      <div v-if="needGiftAddress" class="row mt-2">
        <div class="field">
          <select name="country" v-model="payload.giftAddress.country" @change="clearGiftAddress()">
            <option v-for="(country, index) in countries" :key="index" :value="country">
              {{ country }}
            </option>
          </select>
        </div>
      </div>
      <div class="row mt-2" v-if="needGiftAddress && payload.giftAddress.country === australia">
        <autocomplete-address
          :key="giftFullAddressKey"
          @addressSelected="getGiftSelectedAddress"
        ></autocomplete-address>
      </div>
      <div v-if="needGiftAddress" class="row mt-2">
        <div class="field">
          <input
            @change="updateGiftAddress()"
            v-model="payload.giftAddress.unitNumber"
            type="text"
            placeholder="Unit number"
            autocomplete="nope"
          />
        </div>
        <div class="field">
          <input
            required
            @change="updateGiftAddress()"
            v-model="payload.giftAddress.streetNumber"
            type="text"
            placeholder="Street number"
            autocomplete="nope"
          />
        </div>
      </div>
      <div v-if="needGiftAddress" class="row mt-2">
        <div class="field">
          <input
            required
            @change="updateGiftAddress()"
            v-model="payload.giftAddress.streetName"
            type="text"
            placeholder="Street name"
            autocomplete="nope"
          />
        </div>
        <div class="field">
          <input
            required
            @change="updateGiftAddress()"
            v-model="payload.giftAddress.suburb"
            type="text"
            placeholder="Suburb"
            autocomplete="nope"
          />
        </div>
      </div>
      <div v-if="needGiftAddress" class="row mt-2">
        <div class="field">
          <input
            required
            @change="updateGiftAddress()"
            v-model="payload.giftAddress.postalCode"
            type="text"
            placeholder="Postcode"
            autocomplete="nope"
          />
        </div>
        <div class="field">
          <select
            required
            @change="updateGiftAddress()"
            name="state"
            v-if="payload.giftAddress.country === australia"
            v-model="payload.giftAddress.state"
          >
            <option value disabled selected>Select a state</option>
            <option value="NSW">NSW</option>
            <option value="ACT">ACT</option>
            <option value="NT">NT</option>
            <option value="SA">SA</option>
            <option value="WA">WA</option>
            <option value="VIC">VIC</option>
            <option value="QLD">QLD</option>
            <option value="TAS">TAS</option>
          </select>
          <input
            v-if="payload.giftAddress.country !== australia"
            v-model="payload.giftAddress.state"
            type="text"
            placeholder="State"
            autocomplete="nope"
          />
        </div>
      </div>
      <div class="row mt-2">
        <div class="field">
          <label>Donation Amount</label>
        </div>
      </div>
      <div class="field">
        <div class="row mt-2">
          <input
            required
            type="number"
            :min="payload.donationMinAmount ? payload.donationMinAmount : 0.01"
            v-model="payload.donation.amount"
            step=".01"
          />
        </div>
      </div>

      <!-- DOLLAR HANDLE RANGES -->
      <div
        class="row mt-2"
        v-if="
          payload.donationProductDetails.dollarhandles &&
          Array.isArray(payload.donationProductDetails.dollarhandles[payload.donation.type])
        "
      >
        <div class="field">
          <label>Dollar Handle Ranges</label>
          <div class="row mt-2">
            <div class="flex flex-col">
              <div
                class="flex items-center mb-2"
                v-for="(dollar, index) in payload.donationProductDetails.dollarhandles[payload.donation.type]"
                :key="index"
              >
                <img
                  :class="
                    isInDollarHandleRange(dollar)
                      ? 'rounded border-2 w-24 mr-2 border-primary-500'
                      : 'rounded border-2 w-24 mr-2 border-gray-200'
                  "
                  :src="'https://donate.wwf.org.au' + dollar.img"
                  alt="Image does not exists"
                />
                <button
                  type="button"
                  class="dollar-handle mr-2 flex-1"
                  :class="isInDollarHandleRange(dollar) ? 'primary' : 'secondary'"
                  @click="changeDonationAmountTo(dollar.amount)"
                >
                  ${{ dollar.amount }}
                </button>
                <div :class="isInDollarHandleRange(dollar) ? 'font-bold flex-1' : 'flex-1'">
                  {{ dollar.text }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- PAYMENT MODE -->
      <div class="field mt-4">
        <h2>Payment mode</h2>
        <div class="flex flex-col gap-y-2 ml-8 mt-2">
          <label class="flex flex-row items-center">
            <input
              class="mr-2"
              id="creditCard"
              type="radio"
              :value="creditCard"
              v-model="paymentType"
              @change="changePaymentModeTo(creditCard)"
            />
            Credit Card
          </label>
          <label class="flex flex-row items-center">
            <input
              class="mr-2"
              id="directDebit"
              type="radio"
              :value="directDebit"
              v-model="paymentType"
              @change="changePaymentModeTo(directDebit)"
            />
            Direct Debit
          </label>
          <!-- <label class="flex flex-row items-center" v-if="payload.supporter.phoneNumber">
            <input
              class="mr-2"
              id="sms"
              type="radio"
              :value="sms"
              v-model="paymentType"
              @change="changePaymentModeTo(sms)"
            />
            <span>SMS Pay</span>
          </label> -->
        </div>
      </div>

      <BbmsCreditCardForm v-if="showBbmsPopup" @success="processDonation" @close="showBbmsPopup = false" />
    </div>
    <div class="footer" v-if="!isLoadingForm">
      <button class="secondary" type="button" @click="$emit('previousStep')">Back</button>
      <div class="flex-grow"></div>
      <button type="button" @click="goToNextStep()">
        {{ processButtonLabel }}
      </button>
    </div>
    <Alert v-if="errorMessage" :message="errorMessage"></Alert>
  </form>
</template>

<script>
import countries from '@/assets/countries.json';
import Alert from '@/components/Alert.vue';
import AutocompleteAddress from '@/components/AutocompleteAddress.vue';
import { ALERT_TYPE, MESSAGES } from '@/helpers/constants';
import { mapState } from 'vuex';
import BbmsCreditCardForm from './BbmsCreditCardForm';

export default {
  name: 'DonationDetails',
  components: {
    Alert,
    AutocompleteAddress,
    BbmsCreditCardForm,
  },

  data() {
    return {
      allProducts: null,
      isLoadingForm: true,
      creditCard: 'wwf_bbms',
      directDebit: 'wwf_directdebit',
      sms: 'sms',
      web: 'web',
      paymentType: 'wwf_directdebit',
      selectedPackageIndex: null,
      giftFullAddressKey: 0,
      australia: 'Australia',
      countries: countries.map((country) => country.name),
      showBbmsPopup: false,
      errorMessage: null,
    };
  },
  computed: {
    ...mapState({
      payload: (state) => state.donationModal.payload,
    }),
    hasPackages() {
      return (
        Array.isArray(this.payload.donationProductDetails.packages) &&
        this.payload.donationProductDetails.packages.length > 0
      );
    },
    showGiftDetails() {
      return this.hasPackages && this.payload.donation.isGift;
    },
    selectedPackage() {
      return this.hasPackages ? this.payload.donationProductDetails.packages[this.selectedPackageIndex] : null;
    },
    needGiftAddress() {
      return this.showGiftDetails && this.payload.donation.isGift && !this.payload.donation.isGiftAtSameAddress;
    },
    processButtonLabel() {
      if (this.errorMessage) {
        return 'Try again';
      }

      return this.payload.paymentCaptureSystem === this.sms ? 'Send SMS' : 'Next';
    },
  },

  async beforeMount() {
    try {
      this.allProducts = await this.$store.dispatch('donationModal/getProducts');
      if (!this.payload.donationProductDetails) {
        this.payload.donationProductDetails = this.allProducts[0];
      }
      this.updateDonationSettings();
      this.paymentType = this.payload.donation.paymentType;
    } catch (err) {
      console.error(err);
      if (err && err.status && err.status === 401) {
        this.$store.dispatch('notifications/error', { headline: MESSAGES.NO_PERMISSION });
        this.$router.push('/home');
        this.close();
      }
      this.payload.donationProductDetails = null;
    } finally {
      this.isLoadingForm = false;
    }
  },

  methods: {
    close() {
      this.$store.dispatch('donationModal/close');
      this.$store.dispatch('donationModal/reset');
    },
    toTitleCase(text) {
      const words = text.toLowerCase().split(' ');
      for (let i = 0; i < words.length; i++) {
        words[i] = words[i][0].toUpperCase() + words[i].slice(1);
      }
      return words.join(' ');
    },
    goToNextStep() {
      this.errorMessage = null;
      const isValid = document.querySelector('#donation-details').reportValidity();
      if (isValid) {
        this.payload.donation.amount = Number(this.payload.donation.amount);
        if (this.payload.donation.amount !== this.payload.donation.dollarHandle) {
          this.payload.donation.dollarHandle = null;
        }

        if (this.showGiftDetails && this.payload.donation.isGift) {
          // Transform recipient name to title case
          this.payload.gift.firstName = this.toTitleCase(this.payload.gift.firstName);
          this.payload.gift.lastName = this.toTitleCase(this.payload.gift.lastName);
        } else {
          // Remove all gift information
          this.payload.donation.isGift = false;
          this.clearGiftDetails();
        }
        this.attachProductDetailsToDonation();

        if (this.payload.donation.paymentType === this.creditCard) {
          this.showBbmsPopup = true;
        } else {
          this.$emit('nextStep');
        }
      }
    },
    attachProductDetailsToDonation() {
      const productDetails = this.payload.donationProductDetails;
      this.payload.donation.productId = productDetails.id;
      this.payload.donation.fundId = productDetails.appealcodesetting.fundid;
      this.payload.donation.appealId = productDetails.appealcodesetting.appealid;
      this.payload.donation.campaignId = productDetails.appealcodesetting.campaignid;

      if (productDetails.donationsettings && productDetails.donationsettings[this.payload.donation.type]) {
        const settings = productDetails.donationsettings[this.payload.donation.type];
        this.payload.donation.appealId = settings.appealid ?? this.payload.donation.appealId;
        this.payload.donation.fundId = settings.fundid ?? this.payload.donation.fundId;
        this.payload.donation.campaignId = settings.campaignid ?? this.payload.donation.campaignId;
      }
    },
    parseDonationType(donationType) {
      if (donationType.toLowerCase() === 'single') {
        return 'Give once';
      } else if (donationType.toLowerCase() === 'regular') {
        return 'Give monthly';
      }
      return donationType;
    },
    updateDonationSettings() {
      const dollarHandles = this.payload.donationProductDetails.dollarhandles;
      if (dollarHandles) {
        const dollarHandleTypes = Object.keys(dollarHandles);
        for (const type of dollarHandleTypes) {
          if (Array.isArray(dollarHandles[type])) {
            this.payload.donationProductDetails.dollarhandles[type] = dollarHandles[type].sort((a, b) => {
              const amountA = Number(a.amount);
              const amountB = Number(b.amount);
              if (amountA < amountB) return -1;
              if (amountA >= amountB) return 1;
            });
          }
        }
      }

      if (Array.isArray(this.payload.donationProductDetails.donationtypes)) {
        if (this.payload.donationProductDetails.donationtypes.length === 1 || !this.payload.donation.type) {
          this.payload.donation.type = this.payload.donationProductDetails.donationtypes[0];
        }
        this.getMinDonationAmount();
      }

      // Check package details
      this.selectedPackageIndex = this.hasPackages ? 0 : null;
      this.updatePackageCode();
    },
    getMinDonationAmount() {
      const productDetails = this.payload.donationProductDetails;
      const donationType = this.payload.donation.type;
      if (
        productDetails.donationsettings &&
        productDetails.donationsettings[donationType] &&
        productDetails.donationsettings[donationType].minimumAmount &&
        !isNaN(productDetails.donationsettings[donationType].minimumAmount)
      ) {
        this.payload.donationMinAmount = Number(productDetails.donationsettings[donationType].minimumAmount);
      } else {
        this.payload.donationMinAmount = null;
      }
    },
    isInDollarHandleRange(dollar) {
      const isInDollarHandleRange =
        !isNaN(this.payload.donation.amount) && Number(this.payload.donation.amount) == Number(dollar.amount);
      return isInDollarHandleRange;
    },
    changeDonationAmountTo(value) {
      if (!isNaN(value) && Number(value) > 0) {
        this.payload.donation.amount = value;
        this.payload.donation.dollarHandle = Number(value);
      }
    },
    changePaymentModeTo(type) {
      if (type === this.sms) {
        this.payload.paymentCaptureSystem = this.sms;
        // For now, SMS payment is payment by using credit card
        this.payload.donation.paymentType = this.creditCard;
      } else {
        this.payload.paymentCaptureSystem = this.web;
        this.payload.donation.paymentType = type;
      }
      this.payload.paymentInformation = {
        accountNumber: null,
        accountRouting: null,
        accountTitle: null,
        creditCardToken: null,
        creditCardHolderName: null,
      };
    },
    clearGiftAddress() {
      this.$store.dispatch('donationModal/clearGiftAddress');
    },
    clearGiftDetails() {
      this.$store.dispatch('donationModal/clearGiftDetails');
    },
    getGiftSelectedAddress({ address }) {
      this.payload.giftAddress = address;
      this.payload.giftAddress.unitNumber =
        address.unitNumber && address.unitNumber.trim() !== '' ? address.unitNumber.trim() : null;
    },
    copyGiftAddress() {
      if (this.payload.donation.isGiftAtSameAddress) {
        this.payload.giftAddress = this.payload.billingAddress;
      } else {
        this.clearGiftAddress();
      }
    },
    updateGiftAddress() {
      if (!this.payload.donation.isGiftAtSameAddress) {
        this.$store.dispatch('donationModal/updateGiftAddress');
        // Reset full address input
        this.giftFullAddressKey += 1;
      }
    },
    updatePackageCode() {
      this.payload.donation.packageCode = this.selectedPackage ? this.selectedPackage.code : null;
    },
    errorPackage(packageName) {
      return {
        content: `Package ${packageName} was added but does not exists please create it in cms`,
        type: ALERT_TYPE.ERROR,
      };
    },
    async processDonation(responseData) {
      try {
        this.errorMessage = null;
        this.showBbmsPopup = false;

        this.isLoadingForm = true;
        this.payload.paymentInformation = {
          accountNumber: null,
          accountRouting: null,
          accountTitle: null,
          creditCardToken: responseData.token,
        };

        this.payload.transaction = await this.$store.dispatch(
          'donationModal/createDonationAndProcessPayment',
          this.payload,
        );

        if (this.payload.transaction.status === 'failed') {
          this.payload.paymentInformation = {
            accountNumber: null,
            accountRouting: null,
            accountTitle: null,
            creditCardToken: null,
            creditCardHolderName: null,
          };

          this.errorMessage = {
            content: 'The credit card information is not correct. Try another card.',
            type: ALERT_TYPE.ERROR,
          };

          return;
        }

        // Should move to next step
        this.$emit('moveToStep', 4);
      } catch (err) {
        this.errorMessage = {
          content: 'Payment server is unable to process transaction. Please try again later.',
          type: ALERT_TYPE.ERROR,
        };
        console.error(err);
      } finally {
        this.isLoadingForm = false;
      }
    },
  },
};
</script>

<style>
input[type='text'],
input[type='number'] {
  @apply placeholder-gray-200;
}

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input:disabled {
  @apply bg-gray-200;
}

select:disabled {
  @apply bg-gray-200;
}

.dollar-handle {
  @apply min-w-1/5 max-w-1/5;
}
</style>
